// Header.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { formatDateTime } from '../../utils/formatters';

export default function Header({ onToggleSidebar }) {
  const { user, logout } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [showNotifs, setShowNotifs] = useState(false);
  const notifRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifs(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <style>{`
        .header {
          background: white;
          padding: 14px 24px;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .header-greeting {
          font-size: 17px;
          font-weight: 600;
          color: #0f172a;
          margin: 0;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .menu-toggle-btn {
          display: none;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          padding: 0;
          background: transparent;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 18px;
          cursor: pointer;
          color: #374151;
          transition: all 0.2s ease;
        }

        .menu-toggle-btn:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
        }

        .notif-wrapper {
          position: relative;
        }

        .notif-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: transparent;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
          color: #374151;
          transition: all 0.2s ease;
          position: relative;
        }

        .notif-btn:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
        }

        .notif-btn.active {
          background: #eff6ff;
          border-color: #bfdbfe;
          color: #2563eb;
        }

        .notif-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          background: #dc2626;
          color: white;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          font-size: 10px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
        }

        .notif-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          width: 380px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05);
          z-index: 100;
          max-height: 420px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          animation: notifSlide 0.2s ease-out;
        }

        @keyframes notifSlide {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .notif-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 14px 16px;
          border-bottom: 1px solid #f1f5f9;
          flex-shrink: 0;
        }

        .notif-header-title {
          font-size: 15px;
          font-weight: 600;
          color: #0f172a;
          margin: 0;
        }

        .notif-mark-btn {
          padding: 4px 10px;
          background: none;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 500;
          color: #64748b;
          cursor: pointer;
        }

        .notif-mark-btn:hover {
          background: #f8fafc;
          color: #374151;
        }

        .notif-list {
          overflow-y: auto;
          flex: 1;
          padding: 8px;
        }

        .notif-empty {
          text-align: center;
          padding: 32px 16px;
          color: #94a3b8;
        }

        .notif-empty-icon {
          font-size: 32px;
          margin-bottom: 8px;
        }

        .notif-empty-text {
          font-size: 13px;
          margin: 0;
        }

        .notif-item {
          padding: 10px 12px;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.15s ease;
          margin-bottom: 4px;
        }

        .notif-item:hover {
          background: #f8fafc;
        }

        .notif-item.unread {
          background: #eff6ff;
          border-left: 3px solid #2563eb;
        }

        .notif-item.unread:hover {
          background: #dbeafe;
        }

        .notif-item-title {
          font-size: 13px;
          font-weight: 600;
          color: #0f172a;
          margin: 0 0 3px 0;
        }

        .notif-item-msg {
          font-size: 12px;
          color: #64748b;
          margin: 0 0 4px 0;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .notif-item-time {
          font-size: 11px;
          color: #94a3b8;
          margin: 0;
        }

        .role-badge {
          padding: 4px 10px;
          background: #f1f5f9;
          color: #475569;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
        }

        .logout-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 7px 14px;
          background: #dc2626;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .logout-btn:hover {
          background: #b91c1c;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(220,38,38,0.3);
        }

        .logout-btn:active {
          transform: translateY(0);
        }

        @media (max-width: 768px) {
          .header {
            padding: 12px 16px;
          }
          .menu-toggle-btn {
            display: flex;
          }
          .header-greeting {
            font-size: 15px;
          }
          .notif-dropdown {
            position: fixed;
            top: 60px;
            left: 8px;
            right: 8px;
            width: auto;
            max-height: 70vh;
          }
          .role-badge {
            display: none;
          }
        }

        @media (max-width: 480px) {
          .header-right {
            gap: 8px;
          }
          .notif-btn .notif-text {
            display: none;
          }
          .logout-btn .logout-text {
            display: none;
          }
        }
      `}</style>

      <div className="header">
        <div className="header-left">
          <button className="menu-toggle-btn" onClick={onToggleSidebar} title="Toggle menu">
            ☰
          </button>
          <h2 className="header-greeting">Welcome back, {user?.firstName}</h2>
        </div>

        <div className="header-right">
          <div className="notif-wrapper" ref={notifRef}>
            <button 
              className={`notif-btn ${showNotifs ? 'active' : ''}`} 
              onClick={() => setShowNotifs(!showNotifs)}
            >
              🔔 <span className="notif-text">Notifications</span>
              {unreadCount > 0 && <span className="notif-badge">{unreadCount}</span>}
            </button>

            {showNotifs && (
              <div className="notif-dropdown">
                <div className="notif-header">
                  <p className="notif-header-title">Notifications</p>
                  {unreadCount > 0 && (
                    <button className="notif-mark-btn" onClick={markAllAsRead}>Mark all read</button>
                  )}
                </div>
                <div className="notif-list">
                  {notifications.length === 0 ? (
                    <div className="notif-empty">
                      <div className="notif-empty-icon">🔔</div>
                      <p className="notif-empty-text">No notifications yet</p>
                    </div>
                  ) : (
                    notifications.map(n => (
                      <div 
                        key={n._id} 
                        className={`notif-item ${!n.isRead ? 'unread' : ''}`}
                        onClick={() => markAsRead(n._id)}
                      >
                        <p className="notif-item-title">{n.title}</p>
                        <p className="notif-item-msg">{n.message}</p>
                        <p className="notif-item-time">{formatDateTime(n.createdAt)}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <span className="role-badge">{user?.role?.name}</span>

          <button className="logout-btn" onClick={logout}>
            <span className="logout-text">Logout</span>
          </button>
        </div>
      </div>
    </>
  );
}